/**
 * DEAD SIGNAL — Terminal Input Bar
 *
 * Bottom layer of the three-layer UI. Persistent green monospace
 * input where the player types commands as the chip transmitting
 * to Craine's auditory cortex.
 */
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { AudioSystem } from '../systems/AudioSystem.js';

export default function TerminalBar({ onSubmit, disabled = false }) {
  const [value, setValue] = useState('');
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [showCursor, setShowCursor] = useState(true);
  const inputRef = useRef(null);

  // Blinking cursor timer
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || disabled) return;

    // Add to history
    setHistory((prev) => [trimmed, ...prev].slice(0, 50));
    setHistoryIndex(-1);

    // Submit
    onSubmit(trimmed);
    setValue('');
  }, [value, disabled, onSubmit]);

  const handleKeyDown = useCallback((e) => {
    // Initialize audio context on first interaction. No per-keystroke sound —
    // just one quick clunk when the player submits with Enter.
    AudioSystem.start();
    if (e.key === 'Enter') {
      AudioSystem.playType();
    }

    // History navigation
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHistoryIndex((prev) => {
        const next = Math.min(prev + 1, history.length - 1);
        setValue(history[next] || '');
        return next;
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHistoryIndex((prev) => {
        const next = Math.max(prev - 1, -1);
        setValue(next === -1 ? '' : history[next] || '');
        return next;
      });
    }
  }, [history]);

  // Focus input when clicking anywhere on the bar
  const handleBarClick = useCallback(() => {
    AudioSystem.start();
    inputRef.current?.focus();
  }, []);

  return (
    <div className="terminal-bar" onClick={handleBarClick}>
      <span className="terminal-prompt">LUMEN://&gt;</span>
      <form onSubmit={handleSubmit} style={{ flex: 1, display: 'flex', position: 'relative', alignItems: 'center' }}>
        <input
          ref={inputRef}
          className="terminal-input"
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={disabled ? 'SYSTEM PROCESSING...' : 'transmit to Craine...'}
          disabled={disabled}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          style={{
            position: 'absolute',
            inset: 0,
            opacity: 0,
            zIndex: 2,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        <div
          style={{
            fontFamily: "var(--font-terminal)",
            fontSize: "20px",
            color: "var(--color-terminal)",
            letterSpacing: "0.5px",
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            zIndex: 1,
          }}
        >
          {value ? (
            <>
              {value}
              <span
                style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '18px',
                  background: 'var(--color-terminal)',
                  opacity: showCursor ? 1 : 0,
                  marginLeft: '2px',
                  verticalAlign: 'middle',
                }}
              />
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative', width: '100%' }}>
              <span
                style={{
                  display: 'inline-block',
                  width: '10px',
                  height: '18px',
                  background: 'var(--color-terminal)',
                  opacity: showCursor ? 1 : 0,
                  verticalAlign: 'middle',
                  position: 'absolute',
                  left: 0,
                }}
              />
              <span
                style={{
                  color: "var(--color-terminal-dim)",
                  opacity: 0.4,
                  paddingLeft: '14px',
                }}
              >
                {disabled ? 'SYSTEM PROCESSING...' : 'transmit to Craine...'}
              </span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
